import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../../styles/SplitPaymentModal.css';
import '../../styles/CashRegister.css';

const AMOUNT_TOLERANCE = 0.01;

const SplitPaymentModal = ({
  onClose,
  onConfirm,
  billTotal,
  branch = null,
  registerClosed = false,
}) => {
  const [accounts, setAccounts] = useState([]);
  const [paymentRows, setPaymentRows] = useState([
    { pay_through_account: '', amount: '' },
    { pay_through_account: '', amount: '' },
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPOSAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (branch) {
        params.branch = branch;
      }
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/bank-accounts/pos-accounts`,
        { params }
      );
      setAccounts(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching POS accounts:', err);
      setError('Failed to load payment accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [branch]);

  useEffect(() => {
    fetchPOSAccounts();
  }, [fetchPOSAccounts]);

  const totalAmount = paymentRows.reduce((sum, row) => {
    const amount = parseFloat(row.amount);
    return sum + (isNaN(amount) ? 0 : amount);
  }, 0);

  const remaining = billTotal - totalAmount;
  const isBalanced = Math.abs(remaining) <= AMOUNT_TOLERANCE;

  const usedAccounts = paymentRows
    .map((row) => row.pay_through_account)
    .filter(Boolean);

  const hasDuplicateAccounts =
    new Set(usedAccounts).size !== usedAccounts.length;

  const allRowsValid = paymentRows.every(
    (row) =>
      row.pay_through_account &&
      parseFloat(row.amount) > 0
  );

  const canConfirm =
    !loading &&
    !error &&
    accounts.length > 0 &&
    paymentRows.length >= 2 &&
    allRowsValid &&
    isBalanced &&
    !hasDuplicateAccounts;

  const handleRowChange = (index, field, value) => {
    setPaymentRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const handleAddRow = () => {
    setPaymentRows((prev) => [
      ...prev,
      { pay_through_account: '', amount: '' },
    ]);
  };

  const handleRemoveRow = (index) => {
    if (paymentRows.length <= 2) return;
    setPaymentRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleConfirm = () => {
    if (!canConfirm) return;

    const payments = paymentRows.map((row) => ({
      pay_through_account: row.pay_through_account,
      amount: parseFloat(row.amount),
    }));

    onConfirm(payments);
  };

  const formatAmount = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '0';
    return Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const noBankAccountsAvailable = registerClosed && accounts.length === 0;

  return (
    <div className="modal-overlay">
      <div className="split-payment-modal">
        <div className="modal-header">
          <h2>Split Payment</h2>
          <button className="modal-close" onClick={onClose} type="button">
            ×
          </button>
        </div>

        <div className="modal-body">
          {registerClosed && (
            <div className="register-closed-banner">
              Cash register is closed for {branch || 'this branch'}. Only bank payments are allowed.
            </div>
          )}
          {loading ? (
            <div className="loading-message">Loading accounts...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : noBankAccountsAvailable ? (
            <div className="error-message">
              The cash register is closed and no bank payment accounts are available.
              Please open the register or contact your administrator.
            </div>
          ) : accounts.length === 0 ? (
            <div className="error-message">
              No payment accounts are configured for POS transactions.
              Please contact your administrator.
            </div>
          ) : (
            <>
              <p className="instruction-text">
                Enter the amount for each payment method. The total must match
                the bill amount exactly.
              </p>

              <div className="split-payment-summary">
                <span>
                  <strong>Bill total:</strong> {formatAmount(billTotal)}
                </span>
                <span
                  className={
                    isBalanced
                      ? 'remaining-balance balanced'
                      : 'remaining-balance unbalanced'
                  }
                >
                  <strong>Remaining:</strong> {formatAmount(remaining)}
                </span>
              </div>

              {hasDuplicateAccounts && (
                <div className="error-message inline-error">
                  Each payment method can only be used once.
                </div>
              )}

              <div className="payment-rows">
                {paymentRows.map((row, index) => (
                  <div key={index} className="payment-row">
                    <label className="payment-row-label">Payment {index + 1}</label>
                    <select
                      value={row.pay_through_account}
                      onChange={(e) =>
                        handleRowChange(index, 'pay_through_account', e.target.value)
                      }
                    >
                      <option value="">Select account</option>
                      {accounts.map((account) => (
                        <option
                          key={account.account_number}
                          value={account.account_number}
                        >
                          {account.account_name} ({account.account_number})
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Amount"
                      value={row.amount}
                      onChange={(e) =>
                        handleRowChange(index, 'amount', e.target.value)
                      }
                    />
                    {paymentRows.length > 2 && (
                      <button
                        type="button"
                        className="btn-remove-row"
                        onClick={() => handleRemoveRow(index)}
                        title="Remove payment"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="btn-add-row"
                onClick={handleAddRow}
              >
                + Add another payment
              </button>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleConfirm}
            disabled={!canConfirm}
            type="button"
          >
            Confirm &amp; Process
          </button>
        </div>
      </div>
    </div>
  );
};

export default SplitPaymentModal;
